import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import CommandAnalyticsStrip from '../components/analytics/CommandAnalyticsStrip';
import { AreaTrendChart } from '../components/analytics/CommandCharts';
import api from '../api';
import {
  formatDateTime,
  formatDaysLabel,
  formatNumber,
} from '../utils/formatters';

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 200;
const CHART_PADDING = 14;

function buildLinePath(points = []) {
  if (!points.length) return '';
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ');
}

function buildSparkSeries(points = [], width = CHART_WIDTH, height = CHART_HEIGHT) {
  const max = Math.max(...points.map((point) => Number(point.value || 0)), 1);
  const usableWidth = width - CHART_PADDING * 2;
  const usableHeight = height - CHART_PADDING * 2;
  const step = points.length > 1 ? usableWidth / (points.length - 1) : 0;

  return points.map((point, index) => ({
    ...point,
    x: CHART_PADDING + step * index,
    y: CHART_PADDING + usableHeight - (Number(point.value || 0) / max) * usableHeight,
  }));
}

function scoreSystem(system) {
  const status = String(system.status || '').toLowerCase();
  if (status === 'healthy') return 100;
  if (status === 'warning') return 72;
  if (status === 'pending') return 54;
  return 26;
}

function clusterUptime(systems = [], category) {
  const subset = systems.filter((system) => String(system.category || '').toLowerCase() === category);
  if (!subset.length) return 0;
  const healthy = subset.filter((system) => String(system.status || '').toLowerCase() === 'healthy').length;
  return (healthy / subset.length) * 100;
}

export default function Systems() {
  const navigate = useNavigate();
  const [systems, setSystems] = useState([]);
  const [commandSummary, setCommandSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingCommand, setRefreshingCommand] = useState(false);
  const [error, setError] = useState('');

  const loadSystems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/platform/systems');
      setSystems(response.data.systems || []);
      setCommandSummary(
        response.data.commandAnalytics
          ? {
              ...response.data.commandAnalytics,
              sources: response.data.sources || [],
              freshness: response.data.freshness || null,
              externalSummary: response.data.externalSummary || null,
            }
          : null,
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load systems');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSystems();
  }, [loadSystems]);

  const refreshCommandAnalytics = async () => {
    setRefreshingCommand(true);
    try {
      const { data } = await api.post('/admin/platform/command-analytics/refresh', {
        range: '24h',
      });
      setCommandSummary(data);
      await loadSystems();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not refresh command analytics');
    } finally {
      setRefreshingCommand(false);
    }
  };

  const model = useMemo(() => {
    const orderedSystems = [...systems].sort((left, right) => {
      const leftDate = new Date(left.lastSeenAt || left.lastSyncAt || left.updatedAt || 0).getTime();
      const rightDate = new Date(right.lastSeenAt || right.lastSyncAt || right.updatedAt || 0).getTime();
      return rightDate - leftDate;
    });
    const total = systems.length;
    const healthy = systems.filter((system) => String(system.status || '').toLowerCase() === 'healthy').length;
    const globalUptime = total ? ((healthy / total) * 100).toFixed(1) : '0.0';
    const erpScore = clusterUptime(systems, 'erp').toFixed(1);
    const crmScore = clusterUptime(systems, 'crm').toFixed(1);
    const financeScore = clusterUptime(systems, 'finance').toFixed(1);

    const telemetrySystems = orderedSystems.slice(0, 6).reverse();
    const erpPoints = buildSparkSeries(
      telemetrySystems.map((system, index) => ({
        label: index,
        value: ['erp', 'finance'].includes(String(system.category || '').toLowerCase()) ? scoreSystem(system) : 56,
      }))
    );
    const crmPoints = buildSparkSeries(
      telemetrySystems.map((system, index) => ({
        label: index,
        value: String(system.category || '').toLowerCase() === 'crm' ? scoreSystem(system) : 40,
      }))
    );

    const renewals = [...systems]
      .filter((system) => system.renewalDate)
      .sort((left, right) => new Date(left.renewalDate).getTime() - new Date(right.renewalDate).getTime())
      .slice(0, 3)
      .map((system) => {
        const days = Math.ceil((new Date(system.renewalDate).getTime() - Date.now()) / 86400000);
        return {
          ...system,
          days,
          width: `${Math.max(15, Math.min(90, 100 - Math.max(days, 0) / 2))}%`,
        };
      });

    const errorRows = orderedSystems
      .filter((system) =>
        ['warning', 'offline', 'pending'].includes(String(system.status || '').toLowerCase()) || system.healthMessage
      )
      .slice(0, 4);

    return {
      total,
      globalUptime,
      erpScore,
      crmScore,
      financeScore,
      lastUpdatedAt: orderedSystems[0]?.lastSeenAt || orderedSystems[0]?.lastSyncAt || orderedSystems[0]?.updatedAt || null,
      telemetrySystems,
      erpPath: buildLinePath(erpPoints),
      crmPath: buildLinePath(crmPoints),
      renewals,
      errorRows,
      productionCount: systems.filter((system) => String(system.environment || '').toLowerCase() === 'production').length,
    };
  }, [systems]);

  return (
    <Layout searchPlaceholder="Search system key...">
      <div className="space-y-8">
        {error ? (
          <div className="rounded-lg border border-[#93000a] bg-[#2a1112] px-4 py-3 text-sm text-[#ffdad6]">
            {error}
          </div>
        ) : null}

        {commandSummary ? (
          <CommandAnalyticsStrip
            summary={commandSummary}
            title="Systems command feed"
            description="Shared telemetry from Shimbirr, PMS, Taban Food, and the main Taban Website, normalized into one command stream."
            onRefresh={refreshCommandAnalytics}
            refreshing={refreshingCommand}
            compact
          />
        ) : null}

        <section className="space-y-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#f1c100]">
                Global Overview
              </span>
              <h1 className="mt-2 font-['Geist'] text-[clamp(2rem,5vw,3rem)] font-black tracking-[-0.03em] text-white">
                Systems Monitoring
              </h1>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-3 border border-[#4e4632] bg-[#201f1f] px-4 py-2">
                <span className="status-pulse h-2 w-2 rounded-full bg-[#ffe08b]" />
                <span className="text-sm font-semibold uppercase tracking-[0.14em]">Telemetry Active</span>
              </div>
              <div className="flex items-center gap-3 border border-[#4e4632] bg-[#201f1f] px-4 py-2">
                <span className="text-sm font-semibold uppercase tracking-[0.14em] text-[#d2c5ab]">
                  Last Update: {model.lastUpdatedAt ? formatDateTime(model.lastUpdatedAt) : 'Not recorded'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => navigate('/systems/new')}
                className="glow-yellow rounded-lg bg-[#ffcc00] px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-[#3d2f00] transition hover:brightness-110"
              >
                Register System
              </button>
            </div>
          </div>

          {loading ? (
            <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 text-sm text-[#d2c5ab]">
              Loading systems telemetry...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <StatCard
                  title="Global Uptime"
                  icon="bolt"
                  value={model.globalUptime}
                  suffix="%"
                  meter={`${model.globalUptime}%`}
                />
                <StatCard
                  title="ERP Cluster"
                  icon="database"
                  value={model.erpScore}
                  suffix="%"
                  meter={`${model.erpScore}%`}
                />
                <StatCard
                  title="CRM Bridge"
                  icon="hub"
                  value={model.crmScore}
                  suffix="%"
                  tone="secondary"
                  meter={`${model.crmScore}%`}
                />
                <StatCard
                  title="Finance Core"
                  icon="account_balance"
                  value={model.financeScore}
                  suffix="%"
                  meter={`${model.financeScore}%`}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {commandSummary?.aggregateSeries?.[0]?.points?.length ? (
                  <div className="overflow-hidden rounded-lg border border-[#4e4632] bg-[#1c1b1b] md:col-span-3">
                    <div className="flex items-center justify-between border-b border-[#4e4632] bg-[#201f1f] px-6 py-4">
                      <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white">
                        Cross-product command throughput
                      </h3>
                      <span className="text-xs uppercase tracking-[0.16em] text-[#d2c5ab]">
                        {commandSummary.lastUpdatedAt ? formatDateTime(commandSummary.lastUpdatedAt) : 'Not recorded'}
                      </span>
                    </div>
                    <div className="p-6">
                      <AreaTrendChart
                        points={commandSummary.aggregateSeries[0].points}
                        color="#FFCC00"
                        accent="#FF6D00"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="overflow-hidden rounded-lg border border-[#4e4632] bg-[#1c1b1b] md:col-span-2">
                  <div className="flex items-center justify-between border-b border-[#4e4632] bg-[#201f1f] px-6 py-4">
                    <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white">
                      Uptime Telemetry: Integrated Ecosystem
                    </h3>
                    <div className="flex gap-4">
                      <span className="flex items-center gap-1 text-sm text-[#d2c5ab]">
                        <span className="h-2 w-2 rounded-full bg-[#ffedc3]" /> ERP
                      </span>
                      <span className="flex items-center gap-1 text-sm text-[#d2c5ab]">
                        <span className="h-2 w-2 rounded-full bg-[#fd6c00]" /> CRM
                      </span>
                    </div>
                  </div>
                  <div className="relative h-64 p-6">
                    <svg className="h-full w-full" preserveAspectRatio="none" viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}>
                      {[50, 100, 150].map((line) => (
                        <line key={line} x1="0" x2={CHART_WIDTH} y1={line} y2={line} stroke="#2d2d2d" strokeWidth="1" />
                      ))}
                      <path d={model.erpPath} fill="none" stroke="#ffedc3" strokeWidth="3" />
                      <path d={model.crmPath} fill="none" stroke="#fd6c00" strokeWidth="3" opacity="0.65" />
                    </svg>
                    <div className="absolute bottom-4 right-6 flex gap-2">
                      <span className="bg-[#353534] px-2 py-1 text-xs text-[#d2c5ab]">24H RANGE</span>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-[#4e4632] bg-[#1c1b1b]">
                  <div className="border-b border-[#4e4632] bg-[#201f1f] px-6 py-4">
                    <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white">
                      Renewal Schedules
                    </h3>
                  </div>
                  <div className="flex h-full flex-col gap-6 p-6">
                    {model.renewals.length ? (
                      model.renewals.map((system) => (
                        <div key={system._id} className="space-y-2">
                          <div className="flex justify-between text-xs uppercase tracking-[0.14em] text-[#d2c5ab]">
                            <span>{system.name}</span>
                            <span className="text-white">{formatDaysLabel(system.days).toUpperCase()}</span>
                          </div>
                          <div className="h-4 rounded border border-[#4e4632] bg-[#353534] p-[2px]">
                            <div
                              className={`${system.days <= 14 ? 'bg-[#fd6c00]' : 'bg-[#f1c100]'} h-full rounded-sm`}
                              style={{ width: system.width }}
                            />
                          </div>
                          {system.adminUrl || system.baseUrl ? (
                            <div>
                              <a
                                href={system.adminUrl || system.baseUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#f7d88a] transition hover:text-white"
                              >
                                <MaterialIcon name="open_in_new" className="text-xs" />
                                Open source
                              </a>
                            </div>
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-[#d2c5ab]">No upcoming renewals are tracked yet.</p>
                    )}

                    <button
                      type="button"
                      onClick={() => navigate('/systems/new')}
                      className="mt-auto border border-[#ffcc00] py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#ffcc00] transition hover:bg-[#ffcc00] hover:text-[#3d2f00]"
                    >
                      Open Registry
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="overflow-hidden rounded-lg border border-[#4e4632] bg-[#1c1b1b] md:col-span-3">
                  <div className="flex items-center justify-between border-b border-[#4e4632] bg-[#201f1f] px-6 py-4">
                    <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white">
                      System Error Logs
                    </h3>
                    <div className="flex gap-2">
                      <span className="bg-[#fd6c00] px-2 py-1 text-xs font-bold text-[#341100]">
                        {formatNumber(model.errorRows.filter((row) => String(row.status || '').toLowerCase() !== 'healthy').length)} CRITICAL
                      </span>
                      <span className="bg-[#353534] px-2 py-1 text-xs text-[#d2c5ab]">
                        {formatNumber(model.productionCount)} PROD
                      </span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-[#201f1f]/50 text-[#d2c5ab]">
                        <tr>
                          <th className="px-6 py-3 font-semibold uppercase">Timestamp</th>
                          <th className="px-6 py-3 font-semibold uppercase">Source</th>
                          <th className="px-6 py-3 font-semibold uppercase">Event ID</th>
                          <th className="px-6 py-3 font-semibold uppercase">Payload Message</th>
                          <th className="px-6 py-3 font-semibold uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#4e4632]/30">
                        {model.errorRows.length ? (
                          model.errorRows.map((system, index) => {
                            const isCritical = ['warning', 'offline'].includes(String(system.status || '').toLowerCase());
                            return (
                              <tr key={system._id} className={`transition-colors hover:bg-[#2a2a2a] ${isCritical && index < 2 ? 'bg-[#fd6c00]/10' : ''}`}>
                                <td className={`px-6 py-4 font-mono ${isCritical ? 'text-[#fd6c00]' : 'text-[#d2c5ab]'}`}>
                                  {system.lastSeenAt ? formatDateTime(system.lastSeenAt) : system.updatedAt ? formatDateTime(system.updatedAt) : 'Not recorded'}
                                </td>
                                <td className="px-6 py-4">{system.systemKey?.toUpperCase() || system.name}</td>
                                <td className={`px-6 py-4 ${isCritical ? 'text-[#fd6c00]' : 'text-white'}`}>
                                  #{system._id.slice(-6).toUpperCase()}
                                </td>
                                <td className="px-6 py-4 text-[#e5e2e1]">
                                  {system.healthMessage || `${system.name} is currently reporting ${system.status}.`}
                                </td>
                                <td className="px-6 py-4">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const target = system.adminUrl || system.baseUrl;
                                      if (target) {
                                        window.open(target, '_blank', 'noopener,noreferrer');
                                        return;
                                      }
                                      navigate('/systems/new');
                                    }}
                                    className={`font-bold underline underline-offset-4 ${isCritical ? 'text-[#fd6c00]' : 'text-[#ffcc00]'}`}
                                  >
                                    {system.adminUrl || system.baseUrl
                                      ? 'OPEN SOURCE'
                                      : isCritical
                                        ? 'RECONNECT'
                                        : 'OPTIMIZE'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-6 py-8 text-center text-[#d2c5ab]">
                              No degraded system logs are active right now.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                    <h4 className="mb-4 text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                      Command Center Status
                    </h4>
                    <div className="relative flex aspect-square w-full items-center justify-center rounded-full border-4 border-[#4e4632]">
                      <div className="text-center">
                        <span className="block font-['Geist'] text-4xl font-black text-[#ffedc3]">OP-A</span>
                        <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                          STATION ACTIVE
                        </span>
                      </div>
                      <div className="status-pulse absolute inset-0 scale-75 rounded-full border border-[#ffcc00]/20" />
                    </div>
                  </div>

                  <div className="relative overflow-hidden rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6">
                    <div className="relative z-10">
                      <h4 className="mb-2 text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">
                        Network Topology
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-[#ffedc3]" />
                        <span className="text-sm font-semibold uppercase tracking-[0.14em]">
                          Cloud-Hybrid Mesh
                        </span>
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,204,0,0.08),transparent_42%)] opacity-70" />
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}

function StatCard({ title, icon, value, suffix, meter, tone = 'primary' }) {
  const barTone = tone === 'secondary' ? 'bg-[#fd6c00]' : 'bg-[#f1c100]';
  return (
    <div className={`rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 ${tone === 'primary' ? 'glow-yellow' : ''}`}>
      <div className="mb-4 flex items-start justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">{title}</span>
        <MaterialIcon name={icon} className={tone === 'secondary' ? 'text-[#fd6c00]' : 'text-[#ffedc3]'} />
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-['Geist'] text-5xl font-black text-white">{value}</span>
        <span className={`text-2xl ${tone === 'secondary' ? 'text-[#fd6c00]' : 'text-[#ffedc3]'}`}>{suffix}</span>
      </div>
      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[#353534]">
        <div className={`h-full ${barTone}`} style={{ width: meter }} />
      </div>
    </div>
  );
}
